// AI Service Index
// Unified AI service with automatic model routing

import { AIService } from './interface';
import { GeminiProvider } from './gemini';
import { OpenAIProvider } from './openai';
import { AnthropicProvider, getAnthropicProvider } from './anthropic';
import { AISettings } from '../../types';
import { routeWithOverrides, logUsage, getUsageStats } from './router';
import { AITaskType, TaskContext, AITier, MODEL_CONFIGS, AIProvider, OPENAI_PROVIDER_ENABLED } from '../../types/ai';
import { isAIProxyAvailable } from './proxyClient';

// Export sub-services
export { aiRouter } from './router';
export { whisperService, AudioRecorder, transcribeAudio, parseVoiceIntent } from './whisper';
export { knowledgeService, semanticSearch, storeDocument, findSimilarQuotes } from './knowledge';
export { getAnthropicProvider } from './anthropic';
export type { VoiceIntent, VoiceCommandResult, SemanticSearchResult, SemanticSearchOptions, EmbeddedDocument } from '../../types/ai';

export function getAIProviderInstance(provider: AIProvider): AIService | null {
  switch (provider) {
    case 'OPENAI':
      if (!OPENAI_PROVIDER_ENABLED) {
        return null;
      }
      return getOpenAIProvider();
    case 'ANTHROPIC':
      return getAnthropicProviderInstance();
    case 'GEMINI':
    default:
      return getGeminiProvider();
  }
}

// ============================================================================
// SINGLETON PROVIDERS
// ============================================================================

let geminiInstance: GeminiProvider | null = null;
let openaiInstance: OpenAIProvider | null = null;
let anthropicInstance: AnthropicProvider | null = null;

function getGeminiProvider(): GeminiProvider {
  if (!geminiInstance) {
    geminiInstance = new GeminiProvider();
  }
  return geminiInstance;
}

function getOpenAIProvider(): OpenAIProvider {
  if (!openaiInstance) {
    openaiInstance = new OpenAIProvider();
  }
  return openaiInstance;
}

function getAnthropicProviderInstance(): AnthropicProvider {
  if (!anthropicInstance) {
    anthropicInstance = new AnthropicProvider();
  }
  return anthropicInstance;
}

// ============================================================================
// LEGACY SINGLE PROVIDER MODE
// ============================================================================

const getStoredSettings = (): AISettings | null => {
  try {
    const item = localStorage.getItem('oler_ai_settings');
    if (!item) return null;
    const parsed = JSON.parse(item);
    const sanitized = { provider: parsed?.provider || 'GEMINI', apiKey: '' };
    if (parsed?.apiKey) {
      localStorage.setItem('oler_ai_settings', JSON.stringify(sanitized));
    }
    return sanitized;
  } catch (e) {
    return null;
  }
};

// Legacy singleton for backwards compatibility
let legacyInstance: AIService | null = null;

/**
 * Get AI service (legacy single-provider mode)
 * @deprecated Use getRoutedAIService() for automatic model routing
 * 
 * ⚠️ IMPORTANT: Do not call during component render.
 * Use lazy initialization in event handlers or useEffect hooks.
 */
export const getAIService = (): AIService => {
  // Dev-only: Trace to help identify render-path calls
  if (import.meta.env?.DEV && !legacyInstance) {
    console.trace('[getAIService] Initializing service. Check stack trace to ensure not called during render.');
  }
  
  if (legacyInstance) return legacyInstance;

  // 1. Check LocalStorage Settings first
  const settings = getStoredSettings();
  let providerType = 'GEMINI';

  if (settings && settings.provider) {
    providerType = settings.provider;
  } else {
    // 2. Fallback to Env
    // @ts-ignore
    providerType = import.meta.env?.VITE_AI_PROVIDER || 'GEMINI';
  }

  console.log(`Initializing AI Service with provider: ${providerType}`);

  switch (providerType) {
    case 'OPENAI':
      if (!OPENAI_PROVIDER_ENABLED) {
        console.warn('[AI Service] OpenAI provider disabled. Falling back to Gemini.');
        legacyInstance = getGeminiProvider();
        break;
      }
      legacyInstance = getOpenAIProvider();
      break;
    case 'ANTHROPIC':
      legacyInstance = getAnthropicProviderInstance();
      break;
    case 'GEMINI':
    default:
      legacyInstance = getGeminiProvider();
      break;
  }

  return legacyInstance;
};

/**
 * Reset the AI service instance
 */
export const resetAIService = () => {
  console.log("Resetting AI Service Instances...");
  legacyInstance = null;
  geminiInstance = null;
  openaiInstance = null;
  anthropicInstance = null;
};

// ============================================================================
// ROUTED AI SERVICE
// ============================================================================

/**
 * Check if a provider has API key configured
 */
function isProviderAvailable(provider: AIProvider): boolean {
  try {
    if (provider === 'OPENAI' && !OPENAI_PROVIDER_ENABLED) {
      return false;
    }
    if (provider) {
      return isAIProxyAvailable();
    }
  } catch (e) {
    console.error(`Error checking ${provider} availability:`, e);
  }
  return false;
}

/**
 * Get fallback provider when preferred provider is unavailable
 */
function getFallbackProvider(preferred: AIProvider, tier: AITier): AIProvider {
  // Try providers in order of preference for the tier
  const fallbackOrder: AIProvider[] = 
    tier === 'PREMIUM' 
      ? ['OPENAI', 'GEMINI'] // Premium fallback: OpenAI GPT-4o, then Gemini
      : tier === 'STANDARD'
      ? ['GEMINI', 'OPENAI'] // Standard fallback: Gemini (cheaper), then OpenAI
      : ['GEMINI', 'OPENAI']; // Default: Gemini, then OpenAI

  const effectiveFallback = OPENAI_PROVIDER_ENABLED
    ? fallbackOrder
    : fallbackOrder.filter(provider => provider !== 'OPENAI');
  
  // Remove preferred from fallback list
  const available = effectiveFallback.filter(p => p !== preferred);
  
  for (const provider of available) {
    if (isProviderAvailable(provider)) {
      return provider;
    }
  }
  
  // Last resort: try Gemini (most likely to be configured)
  if (isProviderAvailable('GEMINI')) return 'GEMINI';
  if (OPENAI_PROVIDER_ENABLED && isProviderAvailable('OPENAI')) return 'OPENAI';
  
  // If nothing is available, return preferred anyway (will error with helpful message)
  return preferred;
}

function getDefaultModelForProvider(provider: AIProvider): string {
  switch (provider) {
    case 'ANTHROPIC':
      return MODEL_CONFIGS.PREMIUM.model;
    case 'OPENAI':
      return MODEL_CONFIGS.STANDARD.model;
    case 'GEMINI':
    default:
      return MODEL_CONFIGS.MULTIMODAL.model;
  }
}

/**
 * Get the appropriate AI service based on task context
 * Automatically routes to optimal model with fallback if provider unavailable
 */
export function getRoutedAIService(context: TaskContext): {
  service: AIService;
  tier: AITier;
  provider: AIProvider;
  model: string;
  reason: string;
} {
  const routing = routeWithOverrides(context);
  
  let service: AIService;
  let provider = routing.config.provider;
  let reason = routing.reason;
  
  // Check if preferred provider is available, fallback if not
  // Note: If proxy is available, assume keys are configured via Workers secrets
  if (!isProviderAvailable(provider)) {
    const fallback = getFallbackProvider(provider, routing.config.tier);
    if (fallback !== provider) {
      console.warn(`[AI Router] ${provider} not available, falling back to ${fallback}`);
      provider = fallback;
      reason = `${routing.reason} (fallback: ${provider} - preferred provider unavailable)`;
    }
  }
  
  // Use model from routing config unless provider changed via fallback
  const model = provider === routing.config.provider
    ? routing.config.model
    : getDefaultModelForProvider(provider);
  
  switch (provider) {
    case 'ANTHROPIC':
      service = getAnthropicProviderInstance();
      // Set model on Anthropic provider instance
      if ('setModel' in service && typeof service.setModel === 'function') {
        (service as any).setModel(model);
      }
      break;
    case 'OPENAI':
      service = getOpenAIProvider();
      // Set model on OpenAI provider instance
      if ('setModel' in service && typeof service.setModel === 'function') {
        (service as any).setModel(model);
      }
      break;
    case 'GEMINI':
    default:
      service = getGeminiProvider();
      // Set model on Gemini provider instance
      if ('setModel' in service && typeof service.setModel === 'function') {
        (service as any).setModel(model);
      }
      break;
  }
  
  console.log(`[AI Router] Task: ${context.type}, Tier: ${routing.config.tier}, Provider: ${provider}, Model: ${model}, Reason: ${reason}`);
  
  return {
    service,
    tier: routing.config.tier,
    provider,
    model,
    reason,
  };
}

/**
 * Execute an AI task with automatic routing and usage tracking
 */
// Store last model info for string returns
let lastModelInfo: { provider: AIProvider; model: string; tier: AITier } | null = null;

/**
 * Get the model info from the last AI task execution
 * Useful for string returns that can't have properties attached
 */
export function getLastModelInfo(): { provider: AIProvider; model: string; tier: AITier } | null {
  return lastModelInfo;
}

export async function executeAITask<T>(
  context: TaskContext,
  executor: (service: AIService) => Promise<T>
): Promise<T & { _aiModel?: { provider: AIProvider; model: string; tier: AITier } }> {
  const startTime = Date.now();
  const { service, tier, provider, model } = getRoutedAIService(context);
  
  // Store model info for potential string returns
  lastModelInfo = { provider, model, tier };
  
  try {
    const result = await executor(service);
    
    // Log successful usage
    logUsage({
      taskType: context.type,
      tier,
      inputTokens: 1000, // Estimate - actual would require response parsing
      outputTokens: 500,
      cost: MODEL_CONFIGS[tier] ? 0.01 : 0.005, // Rough estimate
      latencyMs: Date.now() - startTime,
      success: true,
    });
    
    // Attach model info to result if it's an object
    if (result && typeof result === 'object' && !Array.isArray(result)) {
      return {
        ...result,
        _aiModel: { provider, model, tier }
      } as T & { _aiModel?: { provider: AIProvider; model: string; tier: AITier } };
    }
    
    return result;
  } catch (error) {
    // Log failed usage
    logUsage({
      taskType: context.type,
      tier,
      inputTokens: 1000,
      outputTokens: 0,
      cost: MODEL_CONFIGS[tier] ? 0.005 : 0.002, // Partial cost
      latencyMs: Date.now() - startTime,
      success: false,
    });
    
    throw error;
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Get AI for a high-value quote proposal
 */
export function getQuoteAI(quoteValue: number): AIService {
  return getRoutedAIService({ type: 'QUOTE_PROPOSAL', quoteValue }).service;
}

/**
 * Get AI for document analysis
 */
export function getDocumentAI(): AIService {
  return getRoutedAIService({ type: 'DOCUMENT_ANALYSIS', hasImages: true }).service;
}

/**
 * Get AI for strategic analysis
 */
export function getStrategicAI(): AIService {
  return getRoutedAIService({ type: 'WIN_LOSS_ANALYSIS', isStrategic: true }).service;
}

/**
 * Get AI for daily tasks
 */
export function getDailyAI(): AIService {
  return getRoutedAIService({ type: 'DAILY_BRIEFING' }).service;
}

/**
 * Get usage statistics
 */
export function getAIUsageStats(days: number = 30) {
  return getUsageStats(days);
}

// ============================================================================
// PREMIUM FEATURES (Claude-specific)
// ============================================================================

/**
 * Analyze win/loss patterns using premium AI
 */
export async function analyzeWinLossPatterns(quotes: any[]): Promise<any> {
  const anthropic = getAnthropicProviderInstance();
  return anthropic.analyzeWinLossPatterns(quotes);
}

/**
 * Get pricing recommendation using premium AI
 */
export async function getPricingRecommendation(
  quote: any,
  historicalQuotes: any[],
  customerHistory: any[]
): Promise<any> {
  const anthropic = getAnthropicProviderInstance();
  return anthropic.generatePricingRecommendation(quote, historicalQuotes, customerHistory);
}

/**
 * Get strategic insights using premium AI
 */
export async function getStrategicInsights(
  metrics: { revenue: number; winRate: number; avgMargin: number },
  recentActivity: string
): Promise<any> {
  const anthropic = getAnthropicProviderInstance();
  return anthropic.generateStrategicInsights(metrics, recentActivity);
}

export default {
  getAIService,
  getRoutedAIService,
  executeAITask,
  resetAIService,
  getQuoteAI,
  getDocumentAI,
  getStrategicAI,
  getDailyAI,
  getAIUsageStats,
  analyzeWinLossPatterns,
  getPricingRecommendation,
  getStrategicInsights,
};
